import { Product } from '@/api/ProductApi';
import { AssetsLib } from './AssetsLib';
import { ProjectRenderer } from './ProjectRenderer';
import { ObjectName, ThreeEvent, UserDataProp } from './types';
import { Box3, Object3D, Vector3 } from 'three';
import { RenderUtils } from './RenderUtils';

export class ProductBuilder {
	private assetsLib = AssetsLib.getInstance();
	private grateTemplate: Object3D | null = null;
	private grates: Object3D[] = [];
	private gutter: Object3D | null = null;
	private gutterStart: Object3D | null = null;
	private gutterEnd: Object3D | null = null;
	private grateStart: Object3D | null = null;
	private grateEnd: Object3D | null = null;
	private _length = 0;
	private initialLength = 0;
	private gutterSpan = {
		start: 0,
		end: 0,
	};
	private grateSpan = {
		start: 0,
		end: 0,
	};

	static readonly MAX_GRATE_LENGTH = 1.55;

	constructor(private renderer: ProjectRenderer) {}

	get length() {
		return this._length;
	}

	set length(value: number) {
		this._length = value;
		this.applyLength(this._length / this.initialLength, ObjectName.GUTTER);
		this.setStartEndPositions();
		this.calculateSiphonValidXPositions();
		this.computeGrates();
		this.renderer.siphonsManager.fitSiphonsToGutter();
		this.renderer.productMeasures.draw();
	}

	private setStartEndPositions() {
		this.gutterSpan.start = this.gutterStart!.getWorldPosition(new Vector3()).x;
		this.gutterSpan.end = this.gutterEnd!.getWorldPosition(new Vector3()).x;
		this.grateSpan.start = this.grateStart!.getWorldPosition(new Vector3()).x;
		this.grateSpan.end = this.grateEnd!.getWorldPosition(new Vector3()).x;
	}

	private applyLength(scale: number, objectName: ObjectName) {
		const target = this.renderer.product.getObjectByName(objectName);

		if (!target) return;

		target.scale.set(scale, 1, 1);
		target.updateMatrixWorld(true);

		target.traverse((child) => {
			if (child.userData[UserDataProp.REVERT_SCALE] !== objectName) return;

			child.scale.set(1 / scale, 1, 1);
			child.updateMatrixWorld(true);
		});
	}

	private calculateSiphonValidXPositions() {
		const { min, max } = this.renderer.siphonsManager.siphonsXRange;

		this.renderer.emitter.emit(ThreeEvent.SET_OPTIONS, {
			minSiphonsXpos: Math.round(min * 1000),
			maxSiphonsXpos: Math.round(max * 1000),
		});
	}

	private computeGrates() {
		if (!this.grateTemplate) return;

		const box = new Box3().setFromObject(this.grateTemplate);
		const initialGrateLength = box.max.x - box.min.x;

		const quantity = Math.ceil(
			(this.gutterSpan.end - this.gutterSpan.start) /
				ProductBuilder.MAX_GRATE_LENGTH
		);

		const diff = this.grates.length - quantity;

		if (diff > 0)
			for (let i = 0; i < diff; i++) {
				const grate = this.grates.pop();
				if (grate) RenderUtils.disposeObject(grate);
			}

		if (diff < 0)
			for (let i = 0; i < -diff; i++) {
				const grate = this.grateTemplate.clone();
				grate.visible = true;

				this.renderer.product.add(grate);
				this.grates.push(grate);
			}

		const span = this.grateSpan.end - this.grateSpan.start;

		const grateLength = span / this.grates.length;

		const scale = grateLength / initialGrateLength;

		for (let index = 0; index < this.grates.length; index++) {
			const grate = this.grates[index];
			grate.scale.set(scale, 1, 1);
			grate.updateMatrixWorld(true);

			grate.position.setX(
				this.gutterSpan.start + grateLength * index + grateLength / 2
			);
		}
	}

	async loadProduct(product: Product) {
		if (!product.model3d?.model) throw new Error('MISSING_PRODUCT_MODEL');

		const model = await this.assetsLib.getModel(product.model3d.model.path);

		const siphon = model.getObjectByName(ObjectName.SIPHON);
		if (!siphon) throw new Error('MISSING_SIPHON_IN_MODEL');
		siphon.updateMatrixWorld(true);

		const box = new Box3().setFromObject(siphon);
		const syphonWidth = box.max.x - box.min.x;

		this.renderer.siphonsManager.siphonWidth = syphonWidth;
		siphon.visible = false;
		this.renderer.siphonsManager.siphonTemplate = siphon;

		const gutter = model.getObjectByName(ObjectName.GUTTER);
		if (!gutter) throw new Error('MISSING_GUTTER_IN_MODEL');
		this.gutter = gutter;

		box.setFromObject(gutter);
		const gutterWidth = box.max.x - box.min.x;
		this.initialLength = gutterWidth;

		const grate = model.getObjectByName('grate');
		if (!grate) throw new Error('MISSING_GRATE_IN_MODEL');
		this.grateTemplate = grate;
		this.grateTemplate.visible = false;

		const gutterStart = model.getObjectByName(ObjectName.GUTTER_START);
		if (!gutterStart) throw new Error('MISSING_GUTTER_START_IN_MODEL');
		this.gutterStart = gutterStart;

		const gutterEnd = model.getObjectByName(ObjectName.GUTTER_END);
		if (!gutterEnd) throw new Error('MISSING_GUTTER_END_IN_MODEL');
		this.gutterEnd = gutterEnd;

		const grateStart = model.getObjectByName(ObjectName.GRATE_START);
		if (!grateStart) throw new Error('MISSING_GRATE_START_IN_MODEL');
		this.grateStart = grateStart;

		const grateEnd = model.getObjectByName(ObjectName.GRATE_END);
		if (!grateEnd) throw new Error('MISSING_GRATE_END_IN_MODEL');
		this.grateEnd = grateEnd;

		this.renderer.product.add(model);
		this.length = this.initialLength;

		this.renderer.emitter.emit(
			ThreeEvent.SET_LENGTH,
			Math.round(this.length * 1000)
		);
	}

	getAvailableSiphonsAxisData() {
		return {
			xStart: this.gutterSpan.start,
			xEnd: this.gutterSpan.end,
			distance: this.gutterSpan.end - this.gutterSpan.start,
		};
	}

	clear() {
		this.grates = [];
		this.grateTemplate = null;
		this.gutter = null;
		this.gutterStart = null;
		this.gutterEnd = null;
		this.grateStart = null;
		this.grateEnd = null;
		this._length = 0;
		this.initialLength = 0;
		this.gutterSpan = {
			start: 0,
			end: 0,
		};
		this.grateSpan = {
			start: 0,
			end: 0,
		};
	}
}
